import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import { useDispatch, useSelector } from "react-redux";
import ShopRes from "types/res/shop/ShopRes";
import {
  addShop,
  deleteShop,
  fetchShops,
  updateShop,
} from "redux/actions/shop";
import { FormType } from "components/FormField";
import CreateShopReq from "types/req/shop/CreateShopReq";
import UpdateShopReq from "types/req/shop/UpdateShopReq";
import { fetchNomination } from "redux/actions/nomination";
import StaffRole from "../../types/enum/StaffRole";
import EnumUtils from "../../utils/EnumUtils";
import { BusinessCategoryType } from "types/enum/BusinessCategoryType";
import ShopApi from "api/ShopApi";
import { Box, Typography } from "@material-ui/core";
import EnvironmentUtils from "utils/EnviromentUtils";

const Shop: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const shop: ShopRes[] = useSelector((state) => state.shop);
  const companyId = useSelector((state) => state.account.staff.companyId);
  const loginUserRole = useSelector((state) => state.account.staff.role);
  const nominations = useSelector((state) => state.nomination);
  const [useAcdCodeList, setUseAcdCodeList] = useState<string[]>([]);
  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchNomination(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    const apiCall = async () => {
      const result = await ShopApi.acdCodeList(companyId);
      setUseAcdCodeList(result);
    };
    apiCall();
  }, [shop]);
  const headers: { key: keyof ShopRes; label: string }[] = [
    { key: "name", label: t("shop.headers.name") },
    { key: "realName", label: t("shop.headers.realName") },
    { key: "email", label: t("shop.headers.email") },
    { key: "tel", label: t("shop.headers.tel") },
    { key: "url", label: t("shop.headers.url") },
    { key: "cardRate", label: t("shop.headers.cardRate") },
    { key: "pointRate", label: t("shop.headers.pointRate") },
    { key: "orderAlertEmailTime", label: t("shop.headers.orderAlertEmailTime") },
    { key: "openingTime", label: t("shop.headers.openingTime") },
    { key: "closingTime", label: t("shop.headers.closingTime") },
    { key: "businessCategoryType", label: t("shop.headers.businessCategoryType") },
    { key: "nomination", label: t("shop.headers.nomination") },
    { key: "shopGuestKey", label: t("shop.headers.shopGuestKey") },
    { key: "isGuestOrder", label: t("shop.headers.isGuestOrder") },
  ];

  const forms: {
    label: string;
    key: string;
    type: number;
    options?: { id: string | number; name: any }[];
    unSelectOptions?: string[];
  }[] = [
    {
      label: t("shop.fields.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("shop.fields.realName"),
      key: "realName",
      type: FormType.Text,
    },
    {
      label: t("shop.headers.email"),
      key: "email",
      type: FormType.Text,
    },
    {
      label: t("shop.headers.tel"),
      key: "tel",
      type: FormType.Text,
    },
    {
      label: t("shop.headers.url"),
      key: "url",
      type: FormType.Text,
    },
    {
      label: t("shop.headers.cardRate"),
      key: "cardRate",
      type: FormType.Number,
    },
    {
      label: t("shop.headers.pointRate"),
      key: "pointRate",
      type: FormType.Number,
    },
    {
      label: t("shop.headers.orderAlertEmailTime"),
      key: "orderAlertEmailTime",
      type: FormType.Number,
    },
    {
      label: t("shop.headers.openingTime"),
      key: "openingTime",
      type: FormType.Time,
    },
    {
      label: t("shop.headers.closingTime"),
      key: "closingTime",
      type: FormType.Time,
    },
    {
      label: t("shop.headers.businessCategoryType"),
      key: "businessCategoryType",
      type: FormType.Select,
      options: Object.entries(BusinessCategoryType).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    },
    {
      label: t("shop.headers.nomination"),
      key: "nominationId",
      type: FormType.Select,
      options: nominations.map((nomination) => ({
        id: nomination.nominationId,
        name: nomination.name,
      })),
    },
    {
      label: t("shop.headers.isGuestOrder"),
      key: "isGuestOrder",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("shop.options.notAllowed") },
        { id: "1", name: t("shop.options.allowed") },
      ],
    },
  ];
  if (
    [StaffRole.notelMaster, StaffRole.notelCallCenterEmployee].includes(
      EnumUtils.mapToEnum(StaffRole, loginUserRole) ||
        StaffRole.clientShopPartTime
    )
  ) {
    const acdCodeList = [...Array(999)].map((_, i) => ({
      id: String(i).padStart(3, "0"),
      name: String(i).padStart(3, "0"),
    }));
    headers.push({ key: "acdCode", label: t("shop.headers.acdCode") });
    forms.push({
      label: t("shop.headers.acdCode"),
      key: "acdCode",
      type: FormType.Select,
      options: acdCodeList,
      unSelectOptions: useAcdCodeList,
    });
  }

  const deleteAlertComponent = (
    <Box display="flex" flexDirection="column" marginY={2}>
      <Typography style={{ color: "red" }}>
        {t("shop.deleteAlert.warning")}
      </Typography>
      <Typography style={{ marginTop: "8px" }}>{t("shop.deleteAlert.castName")}</Typography>
      <Typography>{t("shop.deleteAlert.course")}</Typography>
      <Typography>{t("shop.deleteAlert.bookEmail")}</Typography>
      <Typography>{t("shop.deleteAlert.callHistory")}</Typography>
      <Typography>{t("shop.deleteAlert.order")}</Typography>
    </Box>
  );
  return (
    <>
      <ShopTable
        // fullWidth
        title={t("shop.title")}
        formId="shopForm"
        forms={forms}
        rows={headers}
        addButtonDataTutorial="shop-add"
        data={shop}
        addType={CreateShopReq}
        updateType={UpdateShopReq}
        addFunc={(formData) => addShop(companyId, formData)}
        updateFunc={(formData) => updateShop(companyId, formData)}
        deleteFunc={(item) => deleteShop(companyId, item.shopId)}
        defaultValue={{ businessCategoryType: "other" }}
        values={[
          (s) => s.name,
          (s) => s.realName,
          (s) => s.email,
          (s) => s.tel,
          (s) => s.url,
          (s) => s.cardRate,
          (s) => s.pointRate,
          (s) => s.orderAlertEmailTime,
          (s) => s.openingTime,
          (s) => s.closingTime,
          (s) =>
            EnumUtils.mapToEnum(BusinessCategoryType, s.businessCategoryType) ||
            t("shop.values.unset"),
          (s) => s?.nomination?.name || t("shop.values.unset"),
          (s) =>
            `${EnvironmentUtils.getGuestDomain()}/register?shopKey=${
              s.shopGuestKey
            }`,
          (s) => (s.isGuestOrder ? t("shop.options.allowed") : t("shop.options.notAllowed")),
          (s) =>
            [
              StaffRole.notelMaster,
              StaffRole.notelCallCenterEmployee,
              StaffRole.notelCallCenterPartTime,
            ].includes(
              EnumUtils.mapToEnum(StaffRole, loginUserRole) ||
                StaffRole.clientShopPartTime
            )
              ? s.acdCode || ""
              : "",
        ]}
        deleteModalText={deleteAlertComponent}
      />
    </>
  );
};

const ShopTable = styled<
  React.FC<CommonTableProps<ShopRes, CreateShopReq, UpdateShopReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Shop;
